import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  NotebookPen,
  Search,
} from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ZeroAumHoldsTab } from '@/components/renewals/ZeroAumHoldsTab';
import { KpiCard } from '@/components/sales/KpiCard';
import { SalesTableState } from '@/components/sales/SalesTableState';
import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import DatePicker from '@/components/ui/DatePicker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useClampPage from '@/hooks/useClampPage';
import useDebounce from '@/hooks/useDebounce';
import usePersistedState from '@/hooks/usePersistedState';
import { useRenewalCalendar, useRenewalMetrics, useRenewalsList } from '@/hooks/useRenewals';
import { inr } from '@/lib/sales-metrics';
import type {
  DueDateNote,
  RenewalCalendarItem,
  RenewalEmailFlags,
  RenewalListItem,
  RenewalPeriodFilter,
  RenewalSortKey,
} from '@/services/renewals.service';
import cn from '@/utils/cn';
import { PAGINATION, RENEWAL_STATUSES, ROUTES, type RenewalStatusValue } from '@/utils/constants';

// ─── Shared helpers ──────────────────────────────────────────────────────────
// Chip tone per derived renewal status: pending=slate, pre-payment email ladder
// =blue, post-payment invoice chain=amber, complete=green, terminated=red.
const STATUS_TONE: Record<RenewalStatusValue, ChipTone> = {
  'Pending Verification': 'neutral',
  'Client Details Confirmed': 'neutral',
  'Renewal Notice Scheduled': 'info',
  'Renewal Notice Sent': 'info',
  'Follow-up 1 Sent': 'info',
  'Follow-up 2 Sent': 'info',
  'Invoice Pending': 'warning',
  'Service Notification Pending': 'warning',
  'Client Invoice Pending': 'warning',
  'Renewal Complete': 'success',
  'Service Terminated': 'destructive',
  Deactivated: 'destructive',
  Skipped: 'neutral',
};

const TERMINAL_STATUSES: readonly RenewalStatusValue[] = [
  'Renewal Complete',
  'Service Terminated',
  'Deactivated',
  'Skipped',
];

// Local (not UTC) YYYY-MM-DD, so "overdue" flips at local midnight like the UI.
const localDateOnly = (d: Date): string => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const fmtDate = (isoDate: string): string => {
  const t = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(t.getTime())) return isoDate;
  return t.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * The heading on a due-date note: "old date -> new date", so the note reads as
 * a decision about the move rather than a comment on a bare date.
 *
 * Falls back to the single date when there is nothing to point away from - a
 * note left without moving anything, or one written before the "from" end was
 * recorded - since "13 Aug 2027 -> 13 Aug 2027" says less than the date alone.
 */
const dueDateNoteHeading = (n: DueDateNote): string => {
  const to = fmtDate(n.dueDate);
  if (!n.fromDueDate || n.fromDueDate.slice(0, 10) === n.dueDate.slice(0, 10)) return to;
  return `${fmtDate(n.fromDueDate)} → ${to}`;
};

const isOverdueUnpaid = (r: RenewalListItem, today: string): boolean =>
  r.dueDate < today && !r.paid && !TERMINAL_STATUSES.includes(r.status);

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const PERIOD_CHIPS: { value: RenewalPeriodFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'nextMonth', label: 'Next Month' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'custom', label: 'Custom Range' },
];

const SORT_OPTIONS: { value: RenewalSortKey; label: string }[] = [
  { value: 'createdAt', label: 'Created' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'amount', label: 'Amount' },
  { value: 'clientName', label: 'Client Name' },
  { value: 'status', label: 'Status' },
];

// ─── Email ladder indicators (Notice / F1 / F2 sent-or-not, per row) ─────────
interface EmailIndicatorsProps {
  flags: RenewalEmailFlags;
}

const EmailIndicators = ({ flags }: EmailIndicatorsProps) => {
  const items = [
    {
      key: 'notice',
      short: 'N',
      label: 'Renewal Notice',
      sent: flags.noticeSent,
    },
    {
      key: 'f1',
      short: 'F1',
      label: 'Follow-up 1',
      sent: flags.followup1Sent,
    },
    {
      key: 'f2',
      short: 'F2',
      label: 'Follow-up 2',
      sent: flags.followup2Sent,
    },
  ];
  return (
    <div className="flex items-center gap-2">
      {items.map((it) => (
        <span
          key={it.key}
          title={`${it.label}: ${it.sent ? 'sent' : 'not sent'}`}
          className="inline-flex items-center gap-0.5"
        >
          {it.sent ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-success" />
          ) : (
            <Circle className="h-3.5 w-3.5 text-muted-foreground/40" />
          )}
          <span
            className={cn(
              'text-[10px] font-semibold',
              it.sent ? 'text-foreground' : 'text-muted-foreground/60',
            )}
          >
            {it.short}
          </span>
        </span>
      ))}
      {flags.terminationSent && (
        <span title="Service termination email sent">
          <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
        </span>
      )}
    </div>
  );
};

// ─── Due-date notes ("date -> note", ADR-054) ────────────────────────────────
// The log explains a chase that is still running, so it is cleared the moment a
// payment is recorded - an empty popup after settling is the intended result,
// not a bug.
interface DueDateNotesDialogProps {
  renewal: RenewalListItem | null;
  onClose: () => void;
}

const DueDateNotesDialog = ({ renewal, onClose }: DueDateNotesDialogProps) => (
  <Dialog
    open={renewal != null}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
  >
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Due Date Notes</DialogTitle>
        <DialogDescription>
          {renewal ? `${renewal.clientName} · ${renewal.productName} · ${renewal.renewalCode}` : ''}
        </DialogDescription>
      </DialogHeader>

      {renewal && renewal.dueDateNotes.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">No notes for this renewal.</p>
      )}

      {renewal && renewal.dueDateNotes.length > 0 && (
        <ul className="max-h-[50vh] space-y-2 overflow-y-auto">
          {renewal.dueDateNotes.map((n) => (
            <li
              key={`${n.at}-${n.dueDate}`}
              className="rounded-lg border border-border bg-muted/30 px-3 py-2"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {dueDateNoteHeading(n)}
              </p>
              <p className="mt-1 text-sm text-foreground">{n.note}</p>
            </li>
          ))}
        </ul>
      )}
    </DialogContent>
  </Dialog>
);

// ─── Table (shared by Pipeline, History and Deactivated) ─────────────────────
interface RenewalsTableProps {
  rows: RenewalListItem[];
  onOpen: (id: number) => void;
  onShowNotes: (row: RenewalListItem) => void;
}

const RenewalsTable = ({ rows, onOpen, onShowNotes }: RenewalsTableProps) => {
  const today = localDateOnly(new Date());
  return (
    <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Renewal</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>ARN</TableHead>
            <TableHead>Product</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Due</TableHead>
            <TableHead>Emails</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.id}
              className="cursor-pointer whitespace-nowrap"
              onClick={() => onOpen(r.id)}
            >
              <TableCell className="font-mono text-xs font-semibold">{r.renewalCode}</TableCell>
              <TableCell>
                <p className="font-semibold">{r.clientName}</p>
                <p className="text-xs text-muted-foreground">{`Cycle ${r.cycleNumber}`}</p>
              </TableCell>
              <TableCell>{r.arn}</TableCell>
              <TableCell>{r.productName}</TableCell>
              <TableCell className="text-right tabular-nums">
                <span className="font-semibold">{r.amount == null ? '-' : inr(r.amount)}</span>
                <span className="ml-1.5 rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  {r.frequency}
                </span>
              </TableCell>
              <TableCell
                className={cn(isOverdueUnpaid(r, today) && 'font-semibold text-destructive')}
              >
                <span className="inline-flex items-center gap-1.5">
                  {fmtDate(r.dueDate)}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 border border-border"
                    title={
                      r.dueDateNotes.length > 0
                        ? `${r.dueDateNotes.length} due date note${r.dueDateNotes.length === 1 ? '' : 's'}`
                        : 'No due date notes'
                    }
                    aria-label="View due date notes"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowNotes(r);
                    }}
                  >
                    <NotebookPen
                      className={cn(
                        'h-3.5 w-3.5',
                        r.dueDateNotes.length > 0 ? 'text-primary' : 'text-muted-foreground/50',
                      )}
                    />
                  </Button>
                </span>
              </TableCell>
              <TableCell>
                <EmailIndicators flags={r.emailFlags} />
              </TableCell>
              <TableCell>
                <StatusChip tone={STATUS_TONE[r.status]}>{r.status}</StatusChip>
              </TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(r.id);
                  }}
                >
                  Open
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// ─── Rows-per-page + range + prev/next (shared) ──────────────────────────────
interface PaginationControlsProps {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  setPage: Dispatch<SetStateAction<number>>;
  setLimit: (limit: number) => void;
}

const PaginationControls = ({
  page,
  totalPages,
  total,
  limit,
  setPage,
  setLimit,
}: PaginationControlsProps) => {
  const fromRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const toRow = Math.min(total, page * limit);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Rows per page</span>
        <div className="w-20">
          <Select value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}>
            {ROWS_PER_PAGE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground">{`${fromRow}–${toRow} of ${total}`}</span>
        <Button
          variant="outline"
          size="icon"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ─── Pipeline tab (server-driven queue) ──────────────────────────────────────
// The Calendar shows chips rather than table rows, so it has no Notes button
// and takes the narrower shape.
interface CalendarTabProps {
  onOpen: (id: number) => void;
}

interface RenewalTabProps extends CalendarTabProps {
  onShowNotes: (row: RenewalListItem) => void;
}

const PipelineTab = ({ onOpen, onShowNotes }: RenewalTabProps) => {
  const [searchInput, setSearchInput] = usePersistedState('renewals:search', '');
  const search = useDebounce(searchInput, 350);
  const [period, setPeriod] = usePersistedState<RenewalPeriodFilter>('renewals:period', 'all');
  // Stored as 'YYYY-MM-DD' strings, never Date objects - a persisted Date comes
  // back from localStorage as a string and breaks every consumer.
  const [customFrom, setCustomFrom] = usePersistedState('renewals:customFrom', '');
  const [customTo, setCustomTo] = usePersistedState('renewals:customTo', '');
  const [status, setStatus] = usePersistedState<RenewalStatusValue | ''>('renewals:status', '');
  // Keys are suffixed v2 so the new soonest-due-first default actually applies:
  // the old keys hold createdAt/desc in every existing biller's localStorage,
  // which would silently override the new default forever.
  const [sortBy, setSortBy] = usePersistedState<RenewalSortKey>('renewals:sortBy:v2', 'dueDate');
  const [sortDir, setSortDir] = usePersistedState<'asc' | 'desc'>('renewals:sortDir:v2', 'asc');
  const [page, setPage] = usePersistedState('renewals:page', 1);
  const [limit, setLimit] = usePersistedState('renewals:limit', 25);

  const { data, isLoading, isError, refetch } = useRenewalsList({
    search: search || undefined,
    status: status || undefined,
    period,
    from: customFrom || undefined,
    to: customTo || undefined,
    sortBy,
    sortDir,
    page,
    limit,
    // The Pipeline is the work list. A subscription that is finished - renewed,
    // cancelled, or upgraded away from - is not work, and after an upgrade the
    // old plan sat here alongside the very plan that replaced it. Those live in
    // History, which asks for them by status. Picking a terminal status in the
    // filter above still shows them here.
    excludeTerminal: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  useClampPage(page, totalPages, !isLoading, setPage);

  return (
    <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search client, ARN, product, RN-…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Status</span>
          <div className="w-52">
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as RenewalStatusValue | '');
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {RENEWAL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
          <span className="text-xs text-muted-foreground">Sort</span>
          <div className="w-36">
            <Select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as RenewalSortKey);
                setPage(1);
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
              setPage(1);
            }}
          >
            {sortDir === 'asc' ? 'Asc' : 'Desc'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {PERIOD_CHIPS.map((p) => (
          <Button
            key={p.value}
            size="sm"
            variant={period === p.value ? 'default' : 'outline'}
            onClick={() => {
              setPeriod(p.value);
              setPage(1);
            }}
          >
            {p.label}
          </Button>
        ))}
        {period === 'custom' && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-2 py-1">
            <DatePicker
              value={customFrom}
              onChange={(v) => {
                setCustomFrom(v);
                setPage(1);
              }}
              placeholder="From"
              className="w-40"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <DatePicker
              value={customTo}
              onChange={(v) => {
                setCustomTo(v);
                setPage(1);
              }}
              placeholder="To"
              className="w-40"
            />
            {(customFrom || customTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCustomFrom('');
                  setCustomTo('');
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </div>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        setPage={setPage}
        setLimit={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      <div className="flex flex-col rounded-xl border border-border bg-card lg:min-h-0 lg:flex-1 lg:overflow-hidden">
        {isLoading && (
          <div className="flex h-full min-h-[320px] items-center justify-center py-14 text-sm text-muted-foreground">
            Loading renewals…
          </div>
        )}
        {!isLoading && isError && (
          <SalesTableState
            variant="error"
            title="Couldn't load renewals"
            description="Something went wrong while fetching the renewals pipeline. Please try again."
            onRetry={() => {
              void refetch();
            }}
            className="h-full min-h-[320px]"
          />
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <SalesTableState
            variant="empty"
            title="No renewals found"
            description="Renewals appear here automatically once billing notifies the service team for a client's products."
            className="h-full min-h-[320px]"
          />
        )}
        {!isLoading && !isError && rows.length > 0 && (
          <RenewalsTable rows={rows} onOpen={onOpen} onShowNotes={onShowNotes} />
        )}
      </div>
    </div>
  );
};

// ─── Calendar tab (CSS-grid month view, Mon→Sun) ─────────────────────────────
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_CHIPS_PER_DAY = 3;

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Selectable years: renewal terms run up to 5 years out, plus a few back for
// history. Computed once at module load.
const CALENDAR_YEARS = (() => {
  const current = new Date().getFullYear();
  return Array.from({ length: 12 }, (_, i) => current - 3 + i);
})();

// Every renewal falling on one date, in a dialog. A day with thirty renewals on
// it cannot be read inside a 96px grid cell, so the cell shows the first few and
// this shows the rest with the detail needed to pick one.
interface CalendarDayDialogProps {
  day: number | null;
  month: number;
  year: number;
  items: RenewalCalendarItem[];
  onClose: () => void;
  onOpen: (id: number) => void;
}

const CalendarDayDialog = ({
  day,
  month,
  year,
  items,
  onClose,
  onOpen,
}: CalendarDayDialogProps) => {
  const label =
    day == null
      ? ''
      : new Date(year, month - 1, day).toLocaleDateString('en-IN', {
          weekday: 'long',
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        });
  return (
    <Dialog
      open={day != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex max-h-[85vh] w-[95vw] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>
            {`${items.length} renewal${items.length === 1 ? '' : 's'} due on this date`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Renewal</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow
                  key={it.id}
                  className="cursor-pointer whitespace-nowrap"
                  onClick={() => {
                    onClose();
                    onOpen(it.id);
                  }}
                >
                  <TableCell className="font-mono text-xs font-semibold">
                    {it.renewalCode}
                  </TableCell>
                  <TableCell>
                    <p className="font-semibold">{it.clientName}</p>
                    <p className="text-xs text-muted-foreground">{it.arn}</p>
                  </TableCell>
                  <TableCell>{it.productName}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">
                    {it.amount == null ? '-' : inr(it.amount)}
                  </TableCell>
                  <TableCell>
                    <StatusChip tone={STATUS_TONE[it.status]}>{it.status}</StatusChip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CalendarTab = ({ onOpen }: CalendarTabProps) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [openDay, setOpenDay] = useState<number | null>(null);

  const { data, isLoading, isError, refetch } = useRenewalCalendar(year, month);
  const items = data ?? [];

  const byDay = useMemo(() => {
    const map = new Map<number, RenewalCalendarItem[]>();
    (data ?? []).forEach((it) => {
      const day = Number(it.dueDate.slice(8, 10));
      const list = map.get(day) ?? [];
      list.push(it);
      map.set(day, list);
    });
    return map;
  }, [data]);

  // Cells for a Monday-first grid, padded to whole weeks. Pre-keyed so no
  // array-index keys leak into JSX.
  const cells = useMemo(() => {
    const offset = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(year, month, 0).getDate();
    const count = Math.ceil((offset + daysInMonth) / 7) * 7;
    return Array.from({ length: count }, (_, i) => {
      const day = i - offset + 1;
      if (day >= 1 && day <= daysInMonth) return { key: `day-${day}`, day };
      return { key: `pad-${i}`, day: null };
    });
  }, [year, month]);

  const shift = (delta: number): void => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };
  const goToday = (): void => {
    const d = new Date();
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const isToday = (day: number): boolean =>
    day === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear();
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label="Previous month"
            onClick={() => shift(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {/* Jump straight to any month/year instead of stepping one at a time -
              renewal due dates sit up to a term (years) out. */}
          <Select
            value={String(month)}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="h-9 w-36"
            aria-label="Month"
          >
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </Select>
          <Select
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 w-24"
            aria-label="Year"
          >
            {CALENDAR_YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
          <Button variant="outline" size="icon" aria-label="Next month" onClick={() => shift(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
        </div>
        <span className="text-sm text-muted-foreground">
          {`${items.length} renewal${items.length === 1 ? '' : 's'} in ${monthLabel}`}
        </span>
      </div>

      <div className="rounded-xl border border-border bg-card p-3">
        {isLoading && (
          <div className="flex min-h-[320px] items-center justify-center text-sm text-muted-foreground">
            Loading calendar…
          </div>
        )}
        {!isLoading && isError && (
          <SalesTableState
            variant="error"
            title="Couldn't load the renewal calendar"
            onRetry={() => {
              void refetch();
            }}
            className="min-h-[320px]"
          />
        )}
        {!isLoading && !isError && (
          <>
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map((d) => (
                <p
                  key={d}
                  className="px-1.5 py-1 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  {d}
                </p>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((cell) => {
                const dayItems = cell.day == null ? [] : (byDay.get(cell.day) ?? []);
                const extra = dayItems.slice(MAX_CHIPS_PER_DAY);
                return (
                  <div
                    key={cell.key}
                    className={cn(
                      'min-h-24 rounded-lg border border-border/60 p-1.5',
                      cell.day == null ? 'bg-muted/30' : 'bg-background',
                      cell.day != null && isToday(cell.day) && 'ring-2 ring-primary',
                    )}
                  >
                    {cell.day != null && (
                      <>
                        {/* The day number opens the full list for that date -
                            the only way to read a day with more renewals than
                            fit in the cell. */}
                        <button
                          type="button"
                          disabled={dayItems.length === 0}
                          onClick={() => setOpenDay(cell.day)}
                          title={
                            dayItems.length > 0
                              ? `View all ${dayItems.length} renewals on this date`
                              : undefined
                          }
                          className={cn(
                            'block w-full rounded px-1 text-right text-xs font-semibold',
                            dayItems.length > 0
                              ? 'text-foreground hover:bg-accent'
                              : 'cursor-default text-muted-foreground',
                          )}
                        >
                          {cell.day}
                        </button>
                        <div className="mt-1 space-y-1">
                          {dayItems.slice(0, MAX_CHIPS_PER_DAY).map((it) => (
                            <button
                              key={it.id}
                              type="button"
                              onClick={() => onOpen(it.id)}
                              title={`${it.clientName} · ${it.productName} · ${it.amount == null ? '' : inr(it.amount)}`}
                              className="block w-full truncate rounded bg-primary/10 px-1.5 py-0.5 text-left text-[11px] font-semibold text-primary hover:bg-primary/20"
                            >
                              {`${it.clientName} · ${it.productName}`}
                            </button>
                          ))}
                          {extra.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setOpenDay(cell.day)}
                              className="block w-full truncate rounded px-1.5 text-left text-[11px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground"
                            >
                              {`+${extra.length} more`}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <CalendarDayDialog
        day={openDay}
        month={month}
        year={year}
        items={openDay == null ? [] : (byDay.get(openDay) ?? [])}
        onClose={() => setOpenDay(null)}
        onOpen={onOpen}
      />
    </div>
  );
};

// ─── History tab (terminal statuses, latest first) ───────────────────────────
// The list endpoint takes a single status value, so the two terminal statuses
// are fetched separately and merged client-side, newest closure first.
const HistoryTab = ({ onOpen, onShowNotes }: RenewalTabProps) => {
  const [page, setPage] = usePersistedState('renewalsHistory:page', 1);
  const [limit, setLimit] = usePersistedState('renewalsHistory:limit', 25);

  const completedQuery = useRenewalsList({
    status: 'Renewal Complete',
    page: 1,
    limit: PAGINATION.MAX_LIMIT,
  });
  const terminatedQuery = useRenewalsList({
    status: 'Service Terminated',
    page: 1,
    limit: PAGINATION.MAX_LIMIT,
  });
  // Skipped renewals (Non-ARN clients, ADKJ-117) are closed like the other two.
  const skippedQuery = useRenewalsList({
    status: 'Skipped',
    page: 1,
    limit: PAGINATION.MAX_LIMIT,
  });

  const isLoading = completedQuery.isLoading || terminatedQuery.isLoading || skippedQuery.isLoading;
  const isError = completedQuery.isError || terminatedQuery.isError || skippedQuery.isError;

  const all = useMemo(() => {
    const merged = [
      ...(completedQuery.data?.rows ?? []),
      ...(terminatedQuery.data?.rows ?? []),
      ...(skippedQuery.data?.rows ?? []),
    ];
    const closedAt = (r: RenewalListItem): string =>
      r.completedAt ?? r.cancelledAt ?? r.skippedAt ?? r.createdAt;
    return merged.sort((a, b) => closedAt(b).localeCompare(closedAt(a)));
  }, [completedQuery.data, terminatedQuery.data, skippedQuery.data]);

  const total = all.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  useClampPage(page, totalPages, !isLoading, setPage);
  const rows = all.slice((page - 1) * limit, page * limit);

  const retry = (): void => {
    void completedQuery.refetch();
    void terminatedQuery.refetch();
    void skippedQuery.refetch();
  };

  return (
    <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">
      <PaginationControls
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        setPage={setPage}
        setLimit={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      <div className="flex flex-col rounded-xl border border-border bg-card lg:min-h-0 lg:flex-1 lg:overflow-hidden">
        {isLoading && (
          <div className="flex h-full min-h-[320px] items-center justify-center py-14 text-sm text-muted-foreground">
            Loading history…
          </div>
        )}
        {!isLoading && isError && (
          <SalesTableState
            variant="error"
            title="Couldn't load renewal history"
            description="Something went wrong while fetching completed and terminated renewals."
            onRetry={retry}
            className="h-full min-h-[320px]"
          />
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <SalesTableState
            variant="empty"
            title="No closed renewals yet"
            description="Completed and terminated renewals appear here."
            className="h-full min-h-[320px]"
          />
        )}
        {!isLoading && !isError && rows.length > 0 && (
          <RenewalsTable rows={rows} onOpen={onOpen} onShowNotes={onShowNotes} />
        )}
      </div>
    </div>
  );
};

// ─── Deactivated tab (grouped by client, ADR-054) ────────────────────────────
// Deactivated subscriptions are not closed - they are waiting on a decision, so
// they sit apart from History. Grouped by client because the question this tab
// answers is "what has this client had switched off?", which a flat product
// list scatters across pages.
const DeactivatedTab = ({ onOpen, onShowNotes }: RenewalTabProps) => {
  const [searchInput, setSearchInput] = usePersistedState('renewalsDeactivated:search', '');
  const search = useDebounce(searchInput, 350);

  const { data, isLoading, isError, refetch } = useRenewalsList({
    search: search || undefined,
    onlyDeactivated: true,
    page: 1,
    limit: PAGINATION.MAX_LIMIT,
    sortBy: 'clientName',
    sortDir: 'asc',
  });

  // Grouped in insertion order rather than sorted again: the server already
  // returns the rows client-name ascending.
  const groups = useMemo(() => {
    type Group = { clientName: string; arn: string; items: RenewalListItem[] };
    const byClient = new Map<number, Group>();
    (data?.rows ?? []).forEach((r) => {
      const group = byClient.get(r.leadId) ?? { clientName: r.clientName, arn: r.arn, items: [] };
      group.items.push(r);
      byClient.set(r.leadId, group);
    });
    return [...byClient.entries()].map(([leadId, g]) => ({ leadId, ...g }));
  }, [data]);

  return (
    <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">
      <div className="relative w-full max-w-md">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search client, ARN, product, RN-…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {isLoading && (
          <div className="flex h-full min-h-[320px] items-center justify-center rounded-xl border border-border bg-card py-14 text-sm text-muted-foreground">
            Loading deactivated subscriptions…
          </div>
        )}
        {!isLoading && isError && (
          <div className="flex flex-col rounded-xl border border-border bg-card">
            <SalesTableState
              variant="error"
              title="Couldn't load deactivated subscriptions"
              description="Something went wrong while fetching them. Please try again."
              onRetry={() => {
                void refetch();
              }}
              className="h-full min-h-[320px]"
            />
          </div>
        )}
        {!isLoading && !isError && groups.length === 0 && (
          <div className="flex flex-col rounded-xl border border-border bg-card">
            <SalesTableState
              variant="empty"
              title="No deactivated subscriptions"
              description="A subscription appears here once both deactivation notices have been sent for it."
              className="h-full min-h-[320px]"
            />
          </div>
        )}

        {!isLoading &&
          !isError &&
          groups.map((g) => (
            <div key={g.leadId} className="rounded-xl border border-destructive/30 bg-card">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                <div>
                  <p className="text-sm font-bold text-foreground">{g.clientName}</p>
                  <p className="text-xs text-muted-foreground">{`ARN ${g.arn}`}</p>
                </div>
                <StatusChip tone="destructive">{`${g.items.length} deactivated`}</StatusChip>
              </div>
              <RenewalsTable rows={g.items} onOpen={onOpen} onShowNotes={onShowNotes} />
            </div>
          ))}
      </div>
    </div>
  );
};

// ─── Page shell: KPI cards + Pipeline | Calendar | History | Deactivated | Zero AUM
type RenewalsTabKey = 'pipeline' | 'calendar' | 'history' | 'deactivated' | 'zeroAum';

const TABS: { key: RenewalsTabKey; label: string }[] = [
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'history', label: 'History' },
  { key: 'deactivated', label: 'Deactivated' },
  { key: 'zeroAum', label: 'Zero AUM' },
];

const RenewalsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = usePersistedState<RenewalsTabKey>('renewals:tab', 'pipeline');
  const [notesRow, setNotesRow] = useState<RenewalListItem | null>(null);

  const metricsQuery = useRenewalMetrics();
  const metrics = metricsQuery.data;
  const metricsLoading = metricsQuery.isLoading;

  const onOpen = (rid: number): void => {
    void navigate(ROUTES.RENEWAL_WORKFLOW(rid));
  };

  const kpis: {
    label: string;
    value: string | number;
    tone: 'success' | 'warning' | 'destructive' | 'info';
  }[] = [
    { label: 'Due This Month', value: metrics?.dueThisMonth ?? 0, tone: 'info' },
    { label: 'Due Next Month', value: metrics?.dueNextMonth ?? 0, tone: 'info' },
    { label: 'Overdue', value: metrics?.overdue ?? 0, tone: 'destructive' },
    { label: 'Payment Pending', value: inr(metrics?.paymentPendingAmount ?? 0), tone: 'warning' },
    { label: 'Renewed (MTD)', value: metrics?.renewedMtd ?? 0, tone: 'success' },
    { label: 'Cancelled (MTD)', value: metrics?.cancelledMtd ?? 0, tone: 'destructive' },
  ];

  return (
    <div className="flex flex-col gap-4 lg:h-full lg:min-h-0">
      <div>
        <h1 className="text-lg font-bold text-foreground">Renewals</h1>
        <p className="text-sm text-muted-foreground">
          Product subscription renewals, created automatically when billing notifies the service
          team. Open one to verify, track its email ladder, and bill it.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((k) => (
          <KpiCard
            key={k.label}
            label={k.label}
            value={metricsLoading ? '-' : k.value}
            tone={k.tone}
          />
        ))}
      </div>

      <div className="inline-flex w-fit rounded-lg border border-border bg-muted/40 p-0.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={
              tab === t.key
                ? 'rounded-md bg-card px-3 py-1.5 text-sm font-semibold text-foreground shadow-sm'
                : 'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && <PipelineTab onOpen={onOpen} onShowNotes={setNotesRow} />}
      {tab === 'calendar' && <CalendarTab onOpen={onOpen} />}
      {tab === 'history' && <HistoryTab onOpen={onOpen} onShowNotes={setNotesRow} />}
      {tab === 'deactivated' && <DeactivatedTab onOpen={onOpen} onShowNotes={setNotesRow} />}
      {tab === 'zeroAum' && <ZeroAumHoldsTab onOpen={onOpen} />}

      <DueDateNotesDialog renewal={notesRow} onClose={() => setNotesRow(null)} />
    </div>
  );
};

export default RenewalsPage;
